import {
  Autocomplete,
  Button,
  Drawer,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DrawerSkeleton from "../helpers/components/DrawerSkeleton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Stack } from "@mui/system";
import EditDefinition from "./EditDefinition";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { UniqueDefinitionsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/UniqueDefinitionsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
type ForceRefreshResult = {
  filteringPayload: string | null;
  lookupPayload: string | null;
};
const EditDefinitionSidebar = ({ open, onClose, loading }: any) => {
  const {
    initialEditData,
    interfaceData,
    destinationTablesData,
    lookupFieldDataForPayload,
    rowFilteringDataForPayload,
    isValidRowFiltering,
    isPreviewEmpty,
    editID,
    isLookupFieldsValid,
  } = useSelector((state: any) => state.uniqueDefinition);
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const interfaceName = initialEditData?.affiliate_system_interface || "";
  const destinationTableName = initialEditData?.destination_table_name || "";
  const [forceRefresh, setForceRefresh] = useState<
    null | (() => Promise<ForceRefreshResult>)
  >(null);

  // Find IDs by matching names with the data
  const intID =
    interfaceData?.find(
      (item: any) => item.affiliate_system_interface_name === interfaceName,
    )?.id || "";

  const destTableID =
    destinationTablesData?.find(
      (item: any) => item.destination_table === destinationTableName,
    )?.id || "";
  const handleEditUniqueDefinition = async () => {
    if (!forceRefresh) return;
    const { filteringPayload, lookupPayload } = await forceRefresh();
    // Validation
    if (!intID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select an interface.",
          type: "error",
        }),
      );
      return;
    }

    if (!destTableID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select a destination table.",
          type: "error",
        }),
      );
      return;
    }

    if (!rowFilteringDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Filtering definition is required.",
          type: "error",
        }),
      );
      return;
    }

    if (!lookupFieldDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unique definition is required.",
          type: "error",
        }),
      );
      return;
    }

    setIsEditing(true);

    try {
      const payload = {
        filtering_definition: filteringPayload,
        unique_definition: lookupPayload,
        backend_interface_id: Number(intID),
        destination_table_id: Number(destTableID),
      };

      const response =
        await UniqueDefinitionsEndpointLogic.updateUnifierUniqueDefinition(
          oauthTokenHeader,
          payload,
          editID,
        );

      if (response?.status === 200) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Unique definition updated successfully!",
            type: "success",
          }),
        );
        onClose();
        await UniqueDefinitionsEndpointLogic.getAllUnifierUniqueDefinitions(
          oauthTokenHeader,
        );
      } else {
        throw new Error(
          response?.reason || "Failed to update unique definition",
        );
      }
    } catch (error: any) {
      console.error("Error upadting unique definition:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.reason ||
            error?.message ||
            "Failed to update unique definition",
          type: "error",
        }),
      );
    } finally {
      setIsEditing(false);
    }
  };
  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      PaperProps={{
        sx: {
          width: "50%",
        },
      }}
    >
      {loading ? (
        <DrawerSkeleton />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 2,
              px: 2,
            }}
          >
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>

            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={initialEditData?.affiliate_system_interface || null}
                    options={[]}
                    disabled
                    renderInput={(params) => (
                      <TextField
                        data-tracker="381"
                        {...params}
                        placeholder="Search interface or choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#00000099",
                      paddingLeft: "12px",
                    }}
                  >
                    All fields are mandatory
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={initialEditData?.destination_table_name || null}
                    options={[]}
                    disabled
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search table or choose from the list"
                        data-tracker="382"
                        label="Destination Table"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#00000099",
                      paddingLeft: "12px",
                    }}
                  >
                    All fields are mandatory
                  </Typography>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  display: "flex",
                }}
              >
                <Button
                  data-tracker="390"
                  variant="contained"
                  color="secondary"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={handleEditUniqueDefinition}
                  disabled={
                    isEditing ||
                    !rowFilteringDataForPayload ||
                    !isLookupFieldsValid ||
                    isPreviewEmpty ||
                    !intID ||
                    !destTableID ||
                    !isValidRowFiltering
                  }
                >
                  <SaveOutlinedIcon sx={{ width: "25px", mr: "5px" }} />
                  Save
                  {isEditing && <SimpleBackdrop />}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <EditDefinition onRegisterForceRefresh={setForceRefresh} />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default EditDefinitionSidebar;
